/**
 * Автопропозиція легкого режиму (UX-36; lite-mode.md, розд. 2). Пропонуємо, якщо виконується хоч
 * одна умова:
 * - `hardwareConcurrency` ≤ 4 або `deviceMemory` ≤ 4 (у Safari й Firefox `deviceMemory` немає);
 * - за перші 2 хв роботи ≥ 5 довгих кадрів понад 200 мс (Long Animation Frames; де їх немає —
 *   довгі задачі);
 * - LCP першого екрана > 4 с два старти поспіль.
 *
 * Виміри — сигнали пристрою в окремому ключі `cl:lite-signals`: це не вибір людини, тож у
 * налаштування (`cl:prefs`, а згодом профіль) вони не йдуть. Рішення про банер приймається до
 * малювання екрана (старт або перехід на інший екран), щоб пізня поява не зсувала вміст (CLS):
 * сигнал, зібраний під час роботи, дає банер на наступному екрані.
 *
 * Тут лише рішення (головний чанк). Виміри — `lite-watch.ts`: вантажаться після `load`, лише коли
 * пропонувати взагалі можна.
 */
import type { Prefs } from './prefs.ts';

export const LITE_OFFER = {
  /** Пропонуємо не частіше разу на тиждень. */
  repeatMs: 7 * 24 * 3_600_000,
  cores: 4,
  memoryGb: 4,
  longFrameMs: 200,
  longFrames: 5,
  /** Вікно підрахунку довгих кадрів від старту сторінки. */
  windowMs: 120_000,
  lcpMs: 4_000,
  lcpStreak: 2,
  /** Скільки живе сигнал довгих кадрів: старий вимір не повинен пропонувати режим вічно. */
  framesTtlMs: 7 * 24 * 3_600_000,
} as const;

export type LiteReason = 'hardware' | 'frames' | 'lcp';

export interface LiteSignals {
  /** Скільки стартів поспіль LCP був понад 4 с. */
  lcpStreak: number;
  /** Коли за перші 2 хв набралося ≥ 5 довгих кадрів (мс epoch); 0 — ні. */
  slowFramesAt: number;
}

export const SIGNALS_KEY = 'cl:lite-signals';

const count = (raw: unknown) =>
  typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? raw : 0;

export function readSignals(): LiteSignals {
  try {
    const raw = JSON.parse(localStorage.getItem(SIGNALS_KEY) ?? '{}') as Partial<LiteSignals>;
    return { lcpStreak: count(raw?.lcpStreak), slowFramesAt: count(raw?.slowFramesAt) };
  } catch {
    return { lcpStreak: 0, slowFramesAt: 0 };
  }
}

export interface NavigatorHints {
  hardwareConcurrency?: number;
  deviceMemory?: number;
}

/** Слабка машина за підказками браузера. */
export function looksWeak(nav: NavigatorHints): boolean {
  const cores = nav.hardwareConcurrency ?? 8;
  const memory = nav.deviceMemory ?? 8;
  return cores <= LITE_OFFER.cores || memory <= LITE_OFFER.memoryGb;
}

/** Чи можна взагалі пропонувати: режим вимкнено, «Не пропонувати» не натиснуто, тиждень минув. */
export function offerAllowed(p: Prefs, now: number): boolean {
  if (p.lite || p.liteOffer === 'never') return false;
  return !(p.liteOffer === 'later' && now - p.liteOfferAt < LITE_OFFER.repeatMs);
}

/** Чому пропонувати легкий режим зараз; `null` — не пропонувати. */
export function liteOfferReason(
  p: Prefs,
  nav: NavigatorHints,
  signals: LiteSignals,
  now: number,
): LiteReason | null {
  if (!offerAllowed(p, now)) return null;
  if (looksWeak(nav)) return 'hardware';
  if (signals.slowFramesAt > 0 && now - signals.slowFramesAt < LITE_OFFER.framesTtlMs)
    return 'frames';
  if (signals.lcpStreak >= LITE_OFFER.lcpStreak) return 'lcp';
  return null;
}
